"use client";
import { useEffect } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Bot, PenTool, MessageCircle } from "lucide-react";
import { AGENTS } from "@/lib/constants";

// Simple enum for chat modes
export enum ChatMode {
  GENERAL = "general",
  GENERAL_V3 = "general_v3",
  DESIGN = "design",
  REQUIREMENT = "requirement",
}

// Simple mode configuration
const CHAT_MODES = {
  [ChatMode.GENERAL_V3]: {
    label: "General Query",
    description: "General Queries and Assistance",
    icon: <MessageCircle className="w-4 h-4" />,
    placeholder: "Ask me anything...",
  },
  [ChatMode.GENERAL]: {
    label: "General Query",
    description: "General Queries and Assistance",
    icon: <MessageCircle className="w-4 h-4" />,
    placeholder: "Ask me anything...",
  },
  [ChatMode.DESIGN]: {
    label: "Design Mode",
    description: "UI/UX design and wireframing assistance",
    icon: <PenTool className="w-4 h-4" />,
    placeholder: "Describe your design requirements...",
  },
  [ChatMode.REQUIREMENT]: {
    label: "Requirement Gathering",
    description: "Gather and analyze project requirements",
    icon: <Bot className="w-4 h-4" />,
    placeholder: "Tell me about your project requirements...",
  },
};

interface Props {
  selectedMode: ChatMode;
  onModeChange: (mode: ChatMode) => void;
  currentAgent?: string;
}

export default function ChatModeSelector({ selectedMode, onModeChange, currentAgent }: Props) {
  // Simple logic: which modes to show based on agent
  const availableModes = Object.entries(CHAT_MODES).filter(([mode]) => {
    if (mode === ChatMode.REQUIREMENT) {
      return false; // Requirement Gathering removed from the dropdown
    }
    if (mode === ChatMode.DESIGN) {
      return currentAgent === AGENTS.DESIGN_AGENT;
    }
    if (mode === ChatMode.GENERAL_V3) {
      return currentAgent === AGENTS.CHAT_AGENT;
    }
    // The chat agent offers the single General Query mode (GENERAL_V3); the
    // other agents use GENERAL as their general mode.
    return currentAgent !== AGENTS.CHAT_AGENT;
  });

  // Auto-switch to the agent's default mode when the current one isn't available
  useEffect(() => {
    const isCurrentModeAvailable = availableModes.some(([mode]) => mode === selectedMode);
    if (!isCurrentModeAvailable) {
      onModeChange(
        currentAgent === AGENTS.CHAT_AGENT ? ChatMode.GENERAL_V3 : ChatMode.GENERAL,
      );
    }
  }, [selectedMode, availableModes, onModeChange, currentAgent]);

  return (
    <Select value={selectedMode} onValueChange={(value) => onModeChange(value as ChatMode)}>
      <SelectTrigger aria-label="Query type" className="min-w-[140px] max-w-[200px] bg-surface-input border-surface-border text-white">
        <div className="flex items-center gap-2 overflow-hidden">
          {CHAT_MODES[selectedMode].icon}
          <span className="truncate text-sm">{CHAT_MODES[selectedMode].label}</span>
        </div>
      </SelectTrigger>
      <SelectContent className="bg-surface-elevated border-surface-border">
        {availableModes.map(([mode, config]) => (
          <SelectItem 
            key={mode}
            value={mode}
            className="text-white hover:bg-surface-hover focus:bg-surface-hover"
          >
            <div className="flex items-center gap-3">
              {config.icon}
              <div className="flex flex-col">
                <span className="font-medium">{config.label}</span>
                <span className="text-xs text-muted">{config.description}</span>
              </div>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Export the modes config so other components can use it
export { CHAT_MODES };